import React, { FC } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { ISubscriptionRoleResult, RoleTypes } from '../types/subscription-role';
import { ErrorOverlayDataGrid } from './ErrorOverlayDataGrid';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import { getConsistentColor } from '../helpers/colorUtils';

interface MembersDataGridProps {
  members: ISubscriptionRoleResult[];
  loading?: boolean;
  error?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
  onDeleteMember: (
    identityId: string,
    memberName: string,
    memberEmail: string,
    roleName: string
  ) => void;
  showDeleteButton?: boolean;
}

const cellSx = { height: '100%', overflow: 'hidden', alignItems: 'flex-start' };

const MembersDataGrid: FC<MembersDataGridProps> = ({
  members,
  loading = false,
  error = false,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onDeleteMember,
  showDeleteButton = true,
}) => {
  const navigate = useNavigate();

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 180,
      flex: 1,
      valueGetter: (_value, row) => `${row.firstName || ''} ${row.lastName || ''}`.trim() || '-',
      renderCell: (params: GridRenderCellParams) => (
        <Stack justifyContent="center" sx={cellSx}>
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap>
              {params.value}
            </Typography>
          </Tooltip>
        </Stack>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Stack justifyContent="center" sx={cellSx}>
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap>
              {params.value}
            </Typography>
          </Tooltip>
        </Stack>
      ),
    },
    {
      field: 'roleName',
      headerName: 'Role',
      minWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const isMainAccount = params.value?.toLowerCase() === RoleTypes.MainAccount;
        return (
          <Stack justifyContent="center" sx={cellSx}>
            {isMainAccount ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <StarOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2">Main account</Typography>
              </Stack>
            ) : (
              <Typography variant="body2">{params.value}</Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'identityId',
      headerName: 'Identity ID',
      minWidth: 280,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ height: '100%', width: '100%', overflow: 'hidden' }}>
          <Tooltip title={`View user details for ${params.value}`}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/user/detail/${params.value}`);
              }}
              sx={{
                cursor: 'pointer',
                overflow: 'hidden',
                flex: 1,
                minWidth: 0,
                '&:hover .link-icon': { opacity: 1 },
              }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: getConsistentColor(params.value),
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                noWrap
                sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                {params.value}
              </Typography>
              <OpenInNewIcon
                className="link-icon"
                sx={{ fontSize: 14, opacity: 0.4, flexShrink: 0, color: 'text.secondary' }}
              />
            </Stack>
          </Tooltip>
          <Tooltip title="Copy">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(params.value);
              }}
              sx={{ flexShrink: 0, ml: 0.5 }}>
              <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
    ...(showDeleteButton
      ? [
          {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            filterable: false,
            headerAlign: 'right' as const,
            align: 'right' as const,
            renderCell: (params: GridRenderCellParams) => {
              const memberName =
                `${params.row.firstName || ''} ${params.row.lastName || ''}`.trim();
              const isMainAccount = params.row.roleName?.toLowerCase() === RoleTypes.MainAccount;
              if (isMainAccount) return null;
              return (
                <Tooltip title="Remove member">
                  <IconButton
                    size="small"
                    onClick={() =>
                      onDeleteMember(
                        params.row.identityId,
                        memberName,
                        params.row.email,
                        params.row.roleName
                      )
                    }
                    data-cy="delete-member-button">
                    <DeleteOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              );
            },
          },
        ]
      : []),
  ];

  if (error) {
    return <ErrorOverlayDataGrid />;
  }

  return (
    <DataGrid
      autoHeight
      columns={columns}
      getRowId={(row) => row.identityId}
      loading={loading}
      pagination
      paginationMode="server"
      rowCount={totalCount}
      paginationModel={{ page, pageSize }}
      onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
        if (newPageSize !== pageSize) {
          onPageSizeChange(newPageSize);
        } else {
          onPageChange(newPage);
        }
      }}
      pageSizeOptions={[5, 10, 20]}
      rows={members || []}
      disableRowSelectionOnClick
      checkboxSelection={false}
      rowHeight={52}
      sx={{ backgroundColor: 'white' }}
    />
  );
};

export default MembersDataGrid;
